import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  MoreVert as MoreIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';

const getPlatformIcon = (platform) => {
  switch (platform.toLowerCase()) {
    case 'twitter':
      return <TwitterIcon />;
    case 'facebook':
      return <FacebookIcon />;
    case 'instagram':
      return <InstagramIcon />;
    case 'linkedin':
      return <LinkedInIcon />;
    default:
      return <MoreIcon />;
  }
};

const getPlatformColor = (platform) => {
  switch (platform.toLowerCase()) {
    case 'twitter':
      return '#1DA1F2';
    case 'facebook':
      return '#4267B2';
    case 'instagram':
      return '#E4405F';
    case 'linkedin':
      return '#0077B5';
    default:
      return '#757575';
  }
};

const getPostTypeLabel = (type) => {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getStatusColor = (status) => {
  switch (status) {
    case 'draft':
      return 'default';
    case 'scheduled':
      return 'warning';
    case 'published':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

const truncateText = (text, maxLength = 120) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const CampaignPostList = ({
  posts,
  selectedPosts,
  onSelectionChange,
  onPostEdit,
  onPostSave
}) => {
  const handlePostSelect = (postId) => {
    if (selectedPosts.includes(postId)) {
      onSelectionChange(selectedPosts.filter(id => id !== postId));
    } else {
      onSelectionChange([...selectedPosts, postId]);
    }
  };

  const handleEditClick = (post, event) => {
    event.stopPropagation();
    onPostEdit(post);
  };

  if (posts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No posts found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          There are no posts matching your current filter.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <List>
        {posts.map((post, index) => {
          const isSelected = selectedPosts.includes(post.id);
          
          return (
            <React.Fragment key={post.id}>
              <ListItem
                disablePadding
                sx={{
                  border: isSelected ? '2px solid' : '1px solid transparent',
                  borderColor: isSelected ? 'primary.main' : 'transparent',
                  borderRadius: 1,
                  mb: 1,
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <ListItemButton onClick={() => handlePostSelect(post.id)}>
                  <ListItemIcon>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handlePostSelect(post.id)}
                        size="small"
                      />
                      <IconButton size="small" sx={{ cursor: 'grab' }}>
                        <DragIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemIcon>

                  <Avatar
                    sx={{
                      bgcolor: getPlatformColor(post.platform),
                      width: 32,
                      height: 32,
                      mr: 2
                    }}
                  >
                    {getPlatformIcon(post.platform)}
                  </Avatar>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          • {getPostTypeLabel(post.postType)}
                        </Typography>
                        <Chip
                          label={post.status}
                          size="small"
                          color={getStatusColor(post.status)}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                          {truncateText(post.content)}
                        </Typography>
                        
                        {post.platforms && post.platforms.length > 1 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Also for:
                            </Typography>
                            {post.platforms
                              .filter(platform => platform !== post.platform)
                              .slice(0, 3) // Show max 3 additional platforms
                              .map((platform) => (
                                <Tooltip key={platform} title={platform}>
                                  <Avatar
                                    sx={{
                                      bgcolor: getPlatformColor(platform),
                                      width: 16,
                                      height: 16,
                                      '& .MuiSvgIcon-root': { fontSize: 10 }
                                    }}
                                  >
                                    {getPlatformIcon(platform)}
                                  </Avatar>
                                </Tooltip>
                              ))
                            }
                            {post.platforms.length > 4 && (
                              <Typography variant="caption" color="text.secondary">
                                +{post.platforms.length - 4} more
                              </Typography>
                            )}
                          </Box>
                        )}

                        {post.scheduledAt && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                            <ScheduleIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              Scheduled for {new Date(post.scheduledAt).toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                  />

                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={(e) => handleEditClick(post, e)}
                      >
                        Edit
                      </Button>
                      
                      <IconButton size="small">
                        <MoreIcon />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItemButton>
              </ListItem>
              
              {index < posts.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default CampaignPostList; 